import React from "react";
import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Layout from "@/components/Layout";

export default function Privacy() {
  return (
    <Layout>
      <div className="px-4 pt-6 pb-8 max-w-3xl mx-auto">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-slate-500 mb-3" data-testid="privacy-back">
          <ArrowLeft className="w-4 h-4" /> Torna alla home
        </Link>
        <h1 className="font-heading font-black text-3xl text-brand-blue" data-testid="privacy-title">Privacy Policy</h1>
        <p className="text-sm text-slate-500 mt-1">Ultimo aggiornamento: [DATA_ULTIMO_AGGIORNAMENTO]</p>

        <div className="prose prose-slate max-w-none mt-6 space-y-6 text-sm sm:text-base leading-relaxed">
          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">1. Titolare del trattamento</h2>
            <p>
              Il titolare del trattamento dei dati personali è <strong>[RAGIONE_SOCIALE — es. Spaccatempo di Nome Cognome]</strong>,
              con sede legale in <strong>[INDIRIZZO_COMPLETO_SEDE_LEGALE]</strong>,
              codice fiscale / partita IVA <strong>[CF_PIVA]</strong> (di seguito, "Spaccatempo").
              Per qualsiasi richiesta relativa al trattamento dei dati puoi contattarci ai recapiti indicati al paragrafo 9.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">2. Dati raccolti tramite ordini e checkout</h2>
            <p>Quando effettui un ordine sul sito raccogliamo i seguenti dati, che tu ci fornisci volontariamente:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>nome e cognome;</li>
              <li>indirizzo email;</li>
              <li>numero di telefono (facoltativo);</li>
              <li>indirizzo di spedizione (via, numero civico, CAP, città, provincia, paese);</li>
              <li>elenco dei prodotti acquistati, quantità e importo dell'ordine;</li>
              <li>identificativo tecnico dell'ordine e riferimento della transazione di pagamento;</li>
              <li>eventuali comunicazioni inviate al nostro servizio clienti.</li>
            </ul>
            <p>
              I dati della carta di credito o di altri strumenti di pagamento <strong>non transitano né vengono conservati sui nostri server</strong>:
              sono gestiti esclusivamente da Stripe (paragrafo 5).
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">3. Finalità e base giuridica</h2>
            <p>I tuoi dati sono trattati per le seguenti finalità:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Evasione dell'ordine</strong>: elaborazione, spedizione, fatturazione e assistenza post-vendita (art. 6.1.b GDPR – esecuzione di un contratto).</li>
              <li><strong>Adempimenti fiscali e contabili</strong> (art. 6.1.c GDPR – obbligo di legge).</li>
              <li><strong>Comunicazioni di servizio</strong>: email di conferma ordine, aggiornamenti sullo stato di spedizione, risposta a richieste (art. 6.1.b GDPR).</li>
              <li><strong>Prevenzione delle frodi</strong> e sicurezza del sistema di pagamento (art. 6.1.f GDPR – legittimo interesse).</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">4. Modalità di trattamento</h2>
            <p>
              I dati sono trattati con strumenti informatici e telematici, adottando misure di sicurezza tecniche e organizzative adeguate a
              prevenirne la perdita, l'uso illecito o non corretto e gli accessi non autorizzati. Gli ordini, i clienti e le sessioni sono
              conservati in un database MongoDB protetto e accessibile solo al personale autorizzato.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">5. Pagamenti – Stripe</h2>
            <p>
              I pagamenti sono elaborati tramite <strong>Stripe Payments Europe, Ltd.</strong>, fornitore di servizi di pagamento certificato PCI-DSS.
              Quando procedi al checkout vieni reindirizzato al form di pagamento sicuro di Stripe: i dati della carta e le informazioni di
              autenticazione sono raccolti e trattati direttamente da Stripe come titolare autonomo/co-titolare del trattamento per le finalità
              di pagamento e antifrode. Riceviamo da Stripe soltanto l'esito e un identificativo tecnico della transazione.
              Per maggiori informazioni: <a className="text-brand-blue underline" href="https://stripe.com/it/privacy" target="_blank" rel="noreferrer">stripe.com/it/privacy</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">6. Integrazione eBay</h2>
            <p>
              Spaccatempo utilizza le API ufficiali eBay per <strong>importare in sola lettura</strong> le inserzioni pubblicate dal nostro account
              venditore (spaccatempotoys) e trasformarle in prodotti acquistabili direttamente sul sito. Nessuna modifica viene apportata alle
              inserzioni eBay attraverso il sito.
            </p>
            <p>
              Il collegamento all'account eBay avviene tramite OAuth 2.0: i token di accesso e refresh sono conservati in forma cifrata
              esclusivamente lato server e non sono mai esposti al frontend.
              Non riceviamo né trattiamo dati personali di clienti eBay: l'importazione riguarda esclusivamente inserzioni prodotto pubbliche.
              Come richiesto dal programma eBay, è attivo un endpoint dedicato alla ricezione di notifiche di
              <em> Marketplace Account Deletion</em>.
              Politica sulla privacy di eBay: <a className="text-brand-blue underline" href="https://www.ebay.it/help/policies/member-behaviour-policies/user-privacy-notice" target="_blank" rel="noreferrer">ebay.it – User Privacy Notice</a>.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">7. Cookie e tecnologie tecniche</h2>
            <p>
              Il sito utilizza esclusivamente <strong>cookie tecnici e archiviazione locale (localStorage)</strong> strettamente necessari al
              funzionamento del servizio, tra cui:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>token di sessione dell'area amministratore (cookie httpOnly);</li>
              <li>contenuto del carrello e dati di checkout compilati (localStorage del browser);</li>
              <li>preferenze tecniche di visualizzazione.</li>
            </ul>
            <p>
              Questi dati restano sul dispositivo dell'utente e non vengono utilizzati per profilazione o marketing. Il sito integra Stripe
              per il pagamento, che può impostare propri cookie durante il checkout: consulta la privacy policy di Stripe per i dettagli.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">8. Tempi di conservazione</h2>
            <ul className="list-disc pl-6 space-y-1">
              <li><strong>Dati d'ordine e fatturazione</strong>: 10 anni, come previsto dalla normativa fiscale italiana (art. 2220 c.c.).</li>
              <li><strong>Comunicazioni di assistenza</strong>: fino a 24 mesi dalla chiusura della richiesta.</li>
              <li><strong>Token OAuth eBay</strong>: fino a revoca del collegamento da parte dell'amministratore.</li>
              <li><strong>Notifiche di Marketplace Account Deletion eBay</strong>: registrate a fini di audit per il tempo minimo richiesto dal programma eBay.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">9. Diritti dell'utente</h2>
            <p>Ai sensi degli artt. 15-22 del Regolamento (UE) 2016/679 (GDPR) hai il diritto di:</p>
            <ul className="list-disc pl-6 space-y-1">
              <li>accedere ai tuoi dati personali;</li>
              <li>chiederne la rettifica o la cancellazione;</li>
              <li>chiederne la limitazione del trattamento;</li>
              <li>opporti al trattamento per motivi legittimi;</li>
              <li>richiedere la portabilità dei dati;</li>
              <li>proporre reclamo all'Autorità Garante per la Protezione dei Dati Personali (<a className="text-brand-blue underline" href="https://www.garanteprivacy.it" target="_blank" rel="noreferrer">garanteprivacy.it</a>).</li>
            </ul>
            <p>Le richieste vanno inviate ai contatti indicati di seguito.</p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">10. Contatti</h2>
            <p>
              Per esercitare i tuoi diritti o per qualsiasi domanda relativa al trattamento dei tuoi dati personali:
            </p>
            <ul className="list-disc pl-6 space-y-1">
              <li>Email: <a className="text-brand-blue underline" href="mailto:spaccatempo1@gmail.com">spaccatempo1@gmail.com</a></li>
              <li>WhatsApp: <a className="text-brand-blue underline" href="https://wa.me/39141912280" target="_blank" rel="noreferrer">+39 141 912 280</a></li>
              <li>Indirizzo postale: [INDIRIZZO_POSTALE_SE_DIVERSO_DA_SEDE_LEGALE]</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-xl text-brand-blue">11. Aggiornamenti</h2>
            <p>
              La presente Privacy Policy può essere aggiornata per riflettere modifiche normative o cambiamenti nel funzionamento del servizio.
              La data dell'ultimo aggiornamento è indicata in alto. Ti invitiamo a consultarla periodicamente.
            </p>
          </section>
        </div>
      </div>
    </Layout>
  );
}
